import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import { API_ENDPOINTS } from '../../../../utils/config';
import {
  Search,
  Filter,
  FileText,
  AlertCircle,
  CheckCircle,
  Eye,
  Edit,
  Trash2,
  Plus,
  CheckCircle2,
  X
} from 'lucide-react';

const Receivables = () => {
  const [receivables, setReceivables] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAging, setFilterAging] = useState('all');
  const [ordering, setOrdering] = useState('-due_date');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [recordPaymentAmount, setRecordPaymentAmount] = useState('');
  const [agedSummary, setAgedSummary] = useState(null);

  const [contactType, setContactType] = useState('customer');
  const [editContactType, setEditContactType] = useState('');
  const [createCustomers, setCreateCustomers] = useState([]);
  const [createClients, setCreateClients] = useState([]);
  const [editCustomers, setEditCustomers] = useState([]);
  const [editClients, setEditClients] = useState([]);

  const [createForm, setCreateForm] = useState({
    invoice: '',
    invoice_number: '',
    invoice_date: '',
    due_date: '',
    amount_due: '',
    amount_paid: '0',
    outstanding_amount: '',
    status: 'outstanding',
    notes: '',
    customer: ''
  });

  const [editForm, setEditForm] = useState({
    invoice: '',
    invoice_number: '',
    invoice_date: '',
    due_date: '',
    amount_due: '',
    amount_paid: '',
    outstanding_amount: '',
    status: 'outstanding',
    notes: '',
    customer: ''
  });

  const getErrorMessage = (err, fallback) => {
    const respData = err?.response?.data;
    const message =
      respData?.message ||
      respData?.detail ||
      respData?.error ||
      (typeof respData === 'string' ? respData : null) ||
      err?.message ||
      fallback;
    if (typeof message === 'string') return message;
    try {
      return JSON.stringify(message || fallback);
    } catch {
      return fallback;
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  const fetchReceivables = async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterAging !== 'all') params.append('aging', filterAging);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', page);
      params.append('ordering', ordering || '-due_date');

      const response = await axios.get(
        `${API_ENDPOINTS.RECEIVABLES_LIST}?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      const data = response.data;
      const normalized = Array.isArray(data) ? data : (data.results || []);
      const normalizedCount = Array.isArray(data) ? data.length : (data.count || normalized.length || 0);

      setReceivables(normalized);
      setTotalCount(normalizedCount);
      setNextPage(Array.isArray(data) ? null : data.next);
      setPrevPage(Array.isArray(data) ? null : data.previous);
      setCurrentPage(page);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch receivables'));
      setReceivables([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAgedSummary = async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.RECEIVABLES_AGED, { headers: getAuthHeaders() });
      setAgedSummary(res.data);
    } catch (err) {
      // ignore summary errors
    }
  };

  const fetchOverdue = async () => {
    try {
      await axios.get(API_ENDPOINTS.RECEIVABLES_OVERDUE, { headers: getAuthHeaders() });
      // not displayed directly; ensures endpoint works
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    fetchReceivables(1);
    fetchAgedSummary();
    fetchOverdue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterAging, ordering]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchReceivables(1);
    }, 400);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const fetchCustomers = async (setter) => {
    try {
      const res = await axios.get(API_ENDPOINTS.CUSTOMERS_LIST, { headers: getAuthHeaders() });
      const data = res.data;
      const normalized = Array.isArray(data) ? data : (data.results || data.data || []);
      setter(normalized);
    } catch (err) {
      // ignore
    }
  };

  const fetchClients = async (setter) => {
    try {
      const res = await axios.get(API_ENDPOINTS.CLIENTS_LIST, { headers: getAuthHeaders() });
      const data = res.data;
      const normalized = Array.isArray(data) ? data : (data.results || data.data || data.clients || []);
      setter(normalized);
    } catch (err) {
      // ignore
    }
  };

  const summary = useMemo(() => {
    const totals = {
      outstanding: 0,
      overdue: 0,
      partially_paid: 0,
      fully_paid: 0
    };
    receivables.forEach((r) => {
      const amt = parseFloat(r.outstanding_amount || r.amount_due || 0) || 0;
      if (r.status === 'overdue') totals.overdue += amt;
      if (r.status === 'partially_paid') totals.partially_paid += amt;
      if (r.status === 'fully_paid') totals.fully_paid += amt;
      if (r.status === 'outstanding') totals.outstanding += amt;
    });
    return totals;
  }, [receivables]);

  const formatCurrency = (value) =>
    `₹${(parseFloat(value || 0) || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  const formatAgingTooltip = () => {
    if (!agedSummary) return 'No aging data';
    const buckets = [
      { key: '0-30 Days', label: '0-30 Days' },
      { key: '31-60 Days', label: '31-60 Days' },
      { key: '61-90 Days', label: '61-90 Days' },
      { key: '90+ Days', label: '90+ Days' }
    ];
    return buckets
      .map((b) => {
        const row = agedSummary[b.key] || {};
        const count = row.count ?? 0;
        const total = row.total_outstanding ?? '0.00';
        return `${b.label}: ${count} invoice(s), ₹${parseFloat(total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} outstanding`;
      })
      .join(' • ');
  };

  const openCreate = () => {
    setCreateForm({
      invoice: '',
      invoice_number: '',
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: new Date().toISOString().split('T')[0],
      amount_due: '',
      amount_paid: '0',
      outstanding_amount: '',
      status: 'outstanding',
      notes: '',
      customer: ''
    });
    setContactType('customer');
    fetchCustomers(setCreateCustomers);
    fetchClients(setCreateClients);
    setIsCreateOpen(true);
    setError('');
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.customer || !contactType) {
      setError('Please select a customer or client for the receivable');
      return;
    }
    setIsCreateLoading(true);
    try {
      const payload = {
        customer: contactType === 'customer' ? parseInt(createForm.customer) : undefined,
        client: contactType === 'client' ? parseInt(createForm.customer) : undefined,
        invoice: createForm.invoice || null,
        invoice_number: createForm.invoice_number || null,
        invoice_date: createForm.invoice_date || null,
        due_date: createForm.due_date || createForm.invoice_date || null,
        amount_due: createForm.amount_due?.toString() || '0',
        amount_paid: createForm.amount_paid?.toString() || '0',
        outstanding_amount: createForm.outstanding_amount?.toString() || createForm.amount_due?.toString() || '0',
        status: createForm.status,
        notes: createForm.notes || ''
      };
      const response = await axios.post(API_ENDPOINTS.RECEIVABLES_LIST, payload, { headers: getAuthHeaders() });
      const msg = response?.data?.message || 'Receivable created successfully';
      setSuccessMessage(msg);
      setIsCreateOpen(false);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchReceivables(currentPage);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create receivable'));
    } finally {
      setIsCreateLoading(false);
    }
  };

  const handleView = async (rec) => {
    try {
      setIsLoading(true);
      const response = await axios.get(API_ENDPOINTS.RECEIVABLES_DETAIL(rec.id), { headers: getAuthHeaders() });
      setSelected(response.data);
      setIsDetailsOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch receivable details'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEdit = async (rec) => {
    try {
      setIsEditLoading(true);
      const response = await axios.get(API_ENDPOINTS.RECEIVABLES_DETAIL(rec.id), { headers: getAuthHeaders() });
      const data = response.data || rec;
      setSelected(data);
      const contactTypeInit = data.customer ? 'customer' : (data.client ? 'client' : '');
      setEditContactType(contactTypeInit || 'customer');
      setEditForm({
        invoice: data.invoice || '',
        invoice_number: data.invoice_number || '',
        invoice_date: data.invoice_date ? data.invoice_date.split('T')[0] : '',
        due_date: data.due_date ? data.due_date.split('T')[0] : (data.invoice_date ? data.invoice_date.split('T')[0] : ''),
        amount_due: data.amount_due || '',
        amount_paid: data.amount_paid || '',
        outstanding_amount: data.outstanding_amount || '',
        status: data.status || 'outstanding',
        notes: data.notes || '',
        customer: data.customer || data.client || ''
      });
      fetchCustomers(setEditCustomers);
      fetchClients(setEditClients);
      setIsEditOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load receivable for editing'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!selected?.id) return;
    if (!editForm.customer && !editForm.invoice) {
      setError('Please provide customer/client or invoice');
      return;
    }
    setIsEditLoading(true);
    try {
      const payload = {
        invoice: editForm.invoice || null,
        invoice_number: editForm.invoice_number || null,
        invoice_date: editForm.invoice_date || null,
        due_date: editForm.due_date || editForm.invoice_date || null,
        amount_due: editForm.amount_due?.toString() || '0',
        amount_paid: editForm.amount_paid?.toString() || '0',
        outstanding_amount: editForm.outstanding_amount?.toString() || '0',
        status: editForm.status,
        notes: editForm.notes || '',
        customer: editContactType !== 'client' && editForm.customer ? parseInt(editForm.customer) : undefined,
        client: editContactType === 'client' && editForm.customer ? parseInt(editForm.customer) : undefined
      };
      const response = await axios.put(
        API_ENDPOINTS.RECEIVABLES_UPDATE(selected.id),
        payload,
        { headers: getAuthHeaders() }
      );
      const msg = response?.data?.message || 'Receivable updated successfully';
      setSuccessMessage(msg);
      setIsEditOpen(false);
      setSelected(null);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchReceivables(currentPage);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update receivable'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleOpenDelete = (rec) => {
    setDeleteTarget(rec);
    setIsDeleteOpen(true);
    setError('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleteLoading(true);
    try {
      const response = await axios.delete(API_ENDPOINTS.RECEIVABLES_DELETE(deleteTarget.id), { headers: getAuthHeaders() });
      const msg = response?.data?.message || 'Receivable deleted successfully';
      setSuccessMessage(msg);
      setIsDeleteOpen(false);
      setDeleteTarget(null);
      fetchReceivables(currentPage);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete receivable'));
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleOpenRecordPayment = (rec) => {
    setSelected(rec);
    setRecordPaymentAmount('');
    setRecordPaymentOpen(true);
    setError('');
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!selected?.id) return;
    if (!recordPaymentAmount) {
      setError('Please enter payment amount');
      return;
    }
    setIsRecording(true);
    try {
      const response = await axios.post(
        API_ENDPOINTS.RECEIVABLES_RECORD_PAYMENT(selected.id),
        { amount: recordPaymentAmount },
        { headers: getAuthHeaders() }
      );
      const msg = response?.data?.message || 'Payment recorded';
      setSuccessMessage(msg);
      setRecordPaymentOpen(false);
      setSelected(null);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchReceivables(currentPage);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to record payment'));
    } finally {
      setIsRecording(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>{successMessage}</span>
          </div>
          <button onClick={() => setSuccessMessage('')} className="text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-muted-foreground">Outstanding</p>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(summary.outstanding)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-muted-foreground">Overdue</p>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(summary.overdue)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-muted-foreground">Partially Paid</p>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(summary.partially_paid)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-muted-foreground">Fully Paid</p>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(summary.fully_paid)}</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search receivables..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="outstanding">Outstanding</option>
            <option value="overdue">Overdue</option>
            <option value="partially_paid">Partially Paid</option>
            <option value="fully_paid">Fully Paid</option>
            <option value="written_off">Written Off</option>
            <option value="disputed">Disputed</option>
          </select>
          <select
            value={filterAging}
            onChange={(e) => setFilterAging(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Aging</option>
            <option value="0_30">0-30</option>
            <option value="31_60">31-60</option>
            <option value="61_90">61-90</option>
            <option value="90_plus">90+</option>
          </select>
          <select
            value={ordering}
            onChange={(e) => setOrdering(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="-due_date">Newest</option>
            <option value="due_date">Oldest</option>
            <option value="-outstanding_amount">Outstanding: High to Low</option>
            <option value="outstanding_amount">Outstanding: Low to High</option>
          </select>
          <Button variant="outline" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2" />
            New Receivable
          </Button>
        </div>
      </div>

      {/* Receivables Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading receivables...</p>
            </div>
          ) : receivables.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-muted-foreground">No receivables found</p>
            </div>
          ) : (
            <>
              <table className="w-full min-w-[1080px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer / Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outstanding</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      title={formatAgingTooltip()}
                    >
                      Aging
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {receivables.map((rec) => (
                    <tr key={rec.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                        {rec.invoice_number || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {rec.customer_name || rec.client_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {rec.due_date ? new Date(rec.due_date).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-foreground">
                        {formatCurrency(rec.outstanding_amount || rec.amount_due)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            rec.status === 'fully_paid'
                              ? 'bg-green-100 text-green-800'
                              : rec.status === 'overdue'
                                ? 'bg-red-100 text-red-800'
                                : rec.status === 'partially_paid'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {rec.status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground capitalize">
                        {rec.aging || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button variant="ghost" size="sm" title="View" onClick={() => handleView(rec)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" title="Edit" onClick={() => handleOpenEdit(rec)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" title="Delete" onClick={() => handleOpenDelete(rec)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Record Payment"
                            onClick={() => handleOpenRecordPayment(rec)}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {(nextPage || prevPage) && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing page {currentPage} of {Math.ceil(totalCount / 10)}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchReceivables(currentPage - 1)}
                      disabled={!prevPage}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchReceivables(currentPage + 1)}
                      disabled={!nextPage}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {isDetailsOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Receivable Details</h3>
                <p className="text-sm text-muted-foreground">Invoice #{selected.invoice_number || 'N/A'}</p>
              </div>
              <Button variant="outline" onClick={() => { setIsDetailsOpen(false); setSelected(null); }}>
                Close
              </Button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-muted-foreground">Customer / Client</p>
                  <p className="font-semibold">{selected.customer_name || selected.client_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <p className="font-semibold capitalize">{selected.status || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Invoice Date</p>
                  <p className="font-semibold">
                    {selected.invoice_date ? new Date(selected.invoice_date).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Due Date</p>
                  <p className="font-semibold">
                    {selected.due_date ? new Date(selected.due_date).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Amount Due</p>
                  <p className="font-semibold">{formatCurrency(selected.amount_due)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Amount Paid</p>
                  <p className="font-semibold">{formatCurrency(selected.amount_paid)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Outstanding</p>
                  <p className="font-semibold">{formatCurrency(selected.outstanding_amount)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Aging</p>
                  <p className="font-semibold">{selected.aging || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Last Payment Date</p>
                  <p className="font-semibold">
                    {selected.last_payment_date ? new Date(selected.last_payment_date).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Last Payment Amount</p>
                  <p className="font-semibold">{formatCurrency(selected.last_payment_amount)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Created</p>
                  <p className="font-semibold">
                    {selected.created_at ? new Date(selected.created_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Updated</p>
                  <p className="font-semibold">
                    {selected.updated_at ? new Date(selected.updated_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
              </div>

              {selected.notes && (
                <div>
                  <p className="text-sm font-semibold text-foreground">Notes</p>
                  <p className="text-sm text-muted-foreground bg-gray-50 border border-gray-200 rounded p-3">
                    {selected.notes}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {isCreateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Create Receivable</h3>
              </div>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Close
              </Button>
            </div>
            <form onSubmit={handleSubmitCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Type</label>
                  <select
                    value={contactType}
                    onChange={(e) => {
                      const v = e.target.value;
                      setContactType(v);
                      setCreateForm((prev) => ({ ...prev, customer: '' }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="customer">Customer</option>
                    <option value="client">Client</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {contactType === 'client' ? 'Client' : 'Customer'}
                  </label>
                  <select
                    value={createForm.customer}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, customer: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select</option>
                    {contactType === 'client'
                      ? createClients.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name || c.client_name || c.company_name || `Client ${c.id}`}
                          </option>
                        ))
                      : createCustomers.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.customer_data?.basic_information?.customer_name ||
                              c.customer_data?.basic_information?.display_name ||
                              c.name ||
                              `Customer ${c.id}`}
                          </option>
                        ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice ID</label>
                  <input
                    type="text"
                    value={createForm.invoice}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, invoice: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                  <input
                    type="text"
                    value={createForm.invoice_number}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, invoice_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={createForm.invoice_date}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, invoice_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={createForm.due_date}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, due_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount Due</label>
                  <input
                    type="number"
                    step="0.01"
                    value={createForm.amount_due}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, amount_due: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid</label>
                  <input
                    type="number"
                    step="0.01"
                    value={createForm.amount_paid}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, amount_paid: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Outstanding</label>
                  <input
                    type="number"
                    step="0.01"
                    value={createForm.outstanding_amount}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, outstanding_amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Auto? fill if known"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={createForm.status}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="outstanding">Outstanding</option>
                    <option value="overdue">Overdue</option>
                    <option value="partially_paid">Partially Paid</option>
                    <option value="fully_paid">Fully Paid</option>
                    <option value="written_off">Written Off</option>
                    <option value="disputed">Disputed</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  rows={3}
                  value={createForm.notes}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Add notes"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isCreateLoading}>
                  {isCreateLoading ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Update Receivable</h3>
                <p className="text-sm text-muted-foreground">Invoice #{selected.invoice_number || 'N/A'}</p>
              </div>
              <Button variant="outline" onClick={() => { setIsEditOpen(false); setSelected(null); }}>
                Close
              </Button>
            </div>
            <form onSubmit={handleSubmitEdit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Type</label>
                  <select
                    value={editContactType}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEditContactType(v);
                      setEditForm((prev) => ({ ...prev, customer: '' }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="customer">Customer</option>
                    <option value="client">Client</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {editContactType === 'client' ? 'Client' : 'Customer'}
                  </label>
                  <select
                    value={editForm.customer}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, customer: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select</option>
                    {editContactType === 'client'
                      ? editClients.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name || c.client_name || c.company_name || `Client ${c.id}`}
                          </option>
                        ))
                      : editCustomers.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.customer_data?.basic_information?.customer_name ||
                              c.customer_data?.basic_information?.display_name ||
                              c.name ||
                              `Customer ${c.id}`}
                          </option>
                        ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice ID</label>
                  <input
                    type="text"
                    value={editForm.invoice}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, invoice: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                  <input
                    type="text"
                    value={editForm.invoice_number}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, invoice_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={editForm.invoice_date}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, invoice_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={editForm.due_date}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, due_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount Due</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.amount_due}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, amount_due: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.amount_paid}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, amount_paid: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Outstanding</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.outstanding_amount}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, outstanding_amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="outstanding">Outstanding</option>
                    <option value="overdue">Overdue</option>
                    <option value="partially_paid">Partially Paid</option>
                    <option value="fully_paid">Fully Paid</option>
                    <option value="written_off">Written Off</option>
                    <option value="disputed">Disputed</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  rows={3}
                  value={editForm.notes}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Update notes"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => { setIsEditOpen(false); setSelected(null); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isEditLoading}>
                  {isEditLoading ? 'Updating...' : 'Update'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {recordPaymentOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Record Payment</h3>
                <p className="text-sm text-muted-foreground">Invoice #{selected.invoice_number || 'N/A'}</p>
              </div>
              <Button variant="outline" onClick={() => { setRecordPaymentOpen(false); setSelected(null); }}>
                Close
              </Button>
            </div>
            <form onSubmit={handleSubmitPayment} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={recordPaymentAmount}
                  onChange={(e) => setRecordPaymentAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter amount"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => { setRecordPaymentOpen(false); setSelected(null); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isRecording}>
                  {isRecording ? 'Recording...' : 'Record Payment'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {isDeleteOpen && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-foreground">Delete Receivable</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Are you sure you want to delete{' '}
                <span className="font-semibold">{deleteTarget.invoice_number || `#${deleteTarget.id}`}</span>?
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setIsDeleteOpen(false); setDeleteTarget(null); }} disabled={isDeleteLoading}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleteLoading}>
                {isDeleteLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Receivables;

